import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';

const testimonials = [
  {
    id: 1,
    name: 'Ananya Iyer',
    role: 'CTO · Meridian Facilities Group',
    logo: 'MERIDIAN',
    text: "YP Nova replaced four vendors for us. Their AI-first Facility ERP cut our ticket resolution time by 42% in the first quarter.",
    avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1'
  },
  {
    id: 2,
    name: 'Marcus Anderson',
    role: 'CEO · Northwind Logistics',
    logo: 'NORTHWIND',
    text: "The kind of engineering partner you actually want in the room. They shipped our visitor management platform in eight weeks &mdash; on scope, on budget.",
    avatar: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1'
  },
  {
    id: 3,
    name: 'James Mitchell',
    role: 'VP Digital · Aurora Health',
    logo: 'AURORA',
    text: "YP Nova\u2019s AI automation platform is the reason our clinical operations team scaled 3x without adding headcount.",
    avatar: 'https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1'
  },
  {
    id: 4,
    name: 'Rachel Foster',
    role: 'Director of IT · Orbit Retail',
    logo: 'ORBIT',
    text: "We\u2019ve worked with the big consultancies. YP Nova moves 3x faster and cares about the outcome, not the invoice.",
    avatar: 'https://images.pexels.com/photos/733872/pexels-photo-733872.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1'
  },
  {
    id: 5,
    name: 'David Zhang',
    role: 'Head of Engineering · Paradigm Manufacturing',
    logo: 'PARADIGM',
    text: "From MIS to work permits to predictive maintenance &mdash; one team, one architecture, real ROI within two quarters.",
    avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1'
  }
];

const QuoteIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-6">
    <path d="M12 18.6667C12 21.6122 9.61218 24 6.66667 24C3.72115 24 1.33333 21.6122 1.33333 18.6667C1.33333 15.7212 3.72115 13.3333 6.66667 13.3333C8.63637 13.3333 10.3582 14.4013 11.2727 16H12V18.6667ZM28 18.6667C28 21.6122 25.6122 24 22.6667 24C19.7212 24 17.3333 21.6122 17.3333 18.6667C17.3333 15.7212 19.7212 13.3333 22.6667 13.3333C24.6364 13.3333 26.3582 14.4013 27.2727 16H28V18.6667Z" fill="#0D212C" fillOpacity="0.2"/>
    <path d="M12 16L12 8C6.10862 8 1.33333 12.7753 1.33333 18.6667" stroke="#0D212C" strokeWidth="2" strokeLinecap="round"/>
    <path d="M28 16L28 8C22.1086 8 17.3333 12.7753 17.3333 18.6667" stroke="#0D212C" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export function TestimonialCarousel() {
  const { ref, isInView } = useInViewAnimation();
  const [currentIndex, setCurrentIndex] = useState(testimonials.length);
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const timeoutRef = useRef(null);

  const extendedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  const nextSlide = () => setCurrentIndex((prev) => prev + 1);
  const prevSlide = () => setCurrentIndex((prev) => prev - 1);

  useEffect(() => {
    if (isHovered) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }
    timeoutRef.current = setTimeout(() => nextSlide(), 3500);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentIndex, isHovered]);

  useEffect(() => {
    if (currentIndex === 0) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(testimonials.length);
      }, 800);
    } else if (currentIndex === extendedTestimonials.length - testimonials.length) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(testimonials.length);
      }, 800);
    } else {
      setIsTransitioning(true);
    }
  }, [currentIndex, extendedTestimonials.length]);

  return (
    <section ref={ref} className="w-full py-20 overflow-hidden">
      <div className="md:max-w-4xl mx-auto md:ml-auto md:mr-0 px-6 mb-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <h2
          className={`text-[32px] md:text-[40px] lg:text-[44px] leading-[1.1] text-[#0D212C] tracking-tight ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
        >
          What <span className="font-serif">enterprise leaders</span> say
        </h2>

        <div
          className={`flex items-center gap-3 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.2s' }}
        >
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="w-5 h-5 fill-black text-black" />
            ))}
          </div>
          <span className="font-medium text-[#0D212C]">4.9 · Clutch</span>
        </div>
      </div>

      <div
        className={`relative ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.3s' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex px-6 md:px-0">
          <div
            className="flex gap-6 w-full"
            style={{
              transform: `translateX(calc(-${currentIndex} * (100% + 24px) / 1))`,
              transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
            }}
          >
            {extendedTestimonials.map((testimonial, idx) => (
              <div
                key={`${testimonial.id}-${idx}`}
                className="flex-shrink-0 w-[calc(100vw-48px)] md:w-[427.5px] bg-white rounded-[32px] md:rounded-[40px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] px-6 md:pl-10 md:pr-24 py-8 flex flex-col justify-between"
                ref={(el) => {
                  if (el) {
                    if (window.innerWidth >= 768) {
                      el.style.transform = 'none';
                      el.parentElement.style.transform = `translateX(calc(-${currentIndex} * (427.5px + 24px)))`;
                    } else {
                      el.parentElement.style.transform = `translateX(calc(-${currentIndex} * (100vw - 48px + 24px)))`;
                    }
                  }
                }}
              >
                <div>
                  <QuoteIcon />
                  <p
                    className="text-base text-[#0D212C] leading-relaxed mb-8"
                    dangerouslySetInnerHTML={{ __html: testimonial.text }}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-sm text-[#0D212C]">{testimonial.name}</h4>
                      <p className="text-xs text-[#273C46]">↗ {testimonial.role}</p>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] tracking-widest text-[#273C46]">{testimonial.logo}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-10">
          <button
            onClick={prevSlide}
            data-testid="testimonial-prev"
            className="w-12 h-12 rounded-full border border-[#0D212C]/20 flex items-center justify-center hover:bg-[#0D212C]/5 transition-colors"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-[#0D212C]" />
          </button>
          <button
            onClick={nextSlide}
            data-testid="testimonial-next"
            className="w-12 h-12 rounded-full border border-[#0D212C]/20 flex items-center justify-center hover:bg-[#0D212C]/5 transition-colors"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-[#0D212C]" />
          </button>
        </div>
      </div>
    </section>
  );
}
